/**
 * resolveServicePlanStep
 *
 * Given a Service Program template and a 1-based visitNumber, returns
 * the most-specific step that matches:
 *   1. Exact visitNumber match
 *   2. Fall back to the "default" (repeating maintenance) step
 *   3. Return null if neither exists
 *
 * @param {object|null} template   — servicePlanTemplate document (has `.steps` array)
 * @param {number}      visitNumber — 1-based integer (completedOccurrences + 1)
 * @returns {object|null}
 */
import { buildItemsSnapshot } from './buildItemsSnapshot';

export function resolveServicePlanStep(template, visitNumber) {
  if (!template?.steps?.length) return null;
  const exact = template.steps.find(s => s.visitNumber === visitNumber);
  if (exact) return exact;
  return template.steps.find(s => s.visitNumber === 'default') || null;
}

/**
 * resolveStepWithItems
 *
 * Same as `resolveServicePlanStep` but ALSO freezes the resolved step's
 * `defaultItems: [{itemId, quantity?}]` into an immutable `items[]`
 * snapshot ready to be written onto a generated visit. Requires the
 * caller to have already loaded the tenant's catalog (via
 * useCatalogItems) so no additional Firestore reads happen at
 * generation time.
 *
 * Returns a NEW object; original template step is not mutated.
 *
 * @param {object|null} template     — service plan template
 * @param {number}      visitNumber   — 1-based
 * @param {object}      catalogById   — { itemId → catalogItem }
 * @returns {object|null} step with `items` populated, or null
 */
export function resolveStepWithItems(template, visitNumber, catalogById) {
  const step = resolveServicePlanStep(template, visitNumber);
  if (!step) return null;
  return {
    ...step,
    items: buildItemsSnapshot(catalogById || {}, step.defaultItems || [], 'program_default'),
  };
}
